import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { v4 } from 'uuid';
import { useTranslation } from 'Common/hooks';

function DefaultCarouselItem({ item }) {
    return <div className="CarouselItem">{item?.toString?.() ?? item}</div>;
}

DefaultCarouselItem.propTypes = {
    item: px.any.isRequired,
};

export default function Carousel({
    animateScroll = true,
    BtnScrollLeft = <i className="fas fa-chevron-left" aria-hidden="true" />,
    BtnScrollRight = <i className="fas fa-chevron-right" aria-hidden="true" />,
    className,
    gap = 0,
    id = v4(),
    Item = DefaultCarouselItem,
    itemProps = {},
    items = [],
    itemsPerView,
    itemWidth,
    keyBy = (x) => x,
    onScroll,
    onSelectItem,
    padding = '2rem',
    scrollToSelected = false,
    selectedIndex = 0,
    style,
    ...props
}) {
    const containerRef = React.useRef();
    const listRef = React.useRef();
    const [selected, setSelected] = React.useState(selectedIndex);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [centerCarousel, setCenterCarousel] = React.useState(false);
    const [visibleIndices, setVisibleIndices] = React.useState([]);

    const leftLbl = useTranslation('Carousel.Button.ScrollLeft.Label');
    const rightLbl = useTranslation('Carousel.Button.ScrollRight.Label');

    const getKey = React.useCallback(
        (item, idx) =>
            typeof keyBy === 'function'
                ? keyBy(item)
                : typeof keyBy === 'string' && item && keyBy in item
                  ? item[keyBy]
                  : `${id}_${idx}`,
        [keyBy, id]
    );

    // Update arrow visibility and visible items based on scroll position
    const updateVisibility = React.useCallback(() => {
        const container = containerRef.current;
        const list = listRef.current;

        if (!container || !list) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        const hasScrollableContent = scrollWidth > clientWidth + 5;

        setCanScrollLeft(hasScrollableContent && scrollLeft > 2);
        setCanScrollRight(hasScrollableContent && scrollLeft + clientWidth < scrollWidth - 2);
        setCenterCarousel(!hasScrollableContent);

        // Calculate which items are visible
        const carouselItems = list.querySelectorAll('.CarouselButton');
        const visible = [];

        carouselItems.forEach((item, idx) => {
            const itemLeft = item.offsetLeft;
            const itemRight = itemLeft + item.offsetWidth;
            const viewStart = scrollLeft;
            const viewEnd = scrollLeft + clientWidth;

            // Item is visible if at least 50% is in view
            const overlapStart = Math.max(viewStart, itemLeft);
            const overlapEnd = Math.min(viewEnd, itemRight);
            const overlapWidth = Math.max(0, overlapEnd - overlapStart);

            if (overlapWidth >= item.offsetWidth * 0.5) {
                visible.push(idx);
            }
        });

        setVisibleIndices((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(visible)) {
                return visible;
            }
            return prev;
        });

        // Call onScroll callback with first visible item index
        if (onScroll && visible.length > 0) {
            onScroll(visible[0], visible.length);
        }
    }, [onScroll]);

    // Listen for scroll events
    React.useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        container.addEventListener('scroll', updateVisibility);
        return () => container.removeEventListener('scroll', updateVisibility);
    }, [updateVisibility]);

    // Update on mount and when items change
    React.useEffect(() => {
        // Delay to ensure DOM is ready
        const timeout = setTimeout(updateVisibility, 50);

        return () => clearTimeout(timeout);
    }, [updateVisibility, items.length]);

    // Update on resize
    React.useEffect(() => {
        window.addEventListener('resize', updateVisibility);
        return () => window.removeEventListener('resize', updateVisibility);
    }, [updateVisibility]);

    // Scroll to a specific item - CSS scroll-snap handles alignment
    const scrollToIndex = React.useCallback(
        (idx) => {
            const container = containerRef.current;
            const list = listRef.current;

            if (!container || !list) return;

            const carouselItems = list.querySelectorAll('.CarouselButton');
            const target = carouselItems[idx];

            if (!target) return;

            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();

            const nextLeft = container.scrollLeft + targetRect.left - containerRect.left;

            container.scrollTo({
                left: nextLeft,
                behavior: animateScroll ? 'smooth' : 'instant',
            });
        },
        [animateScroll]
    );

    // Select an item and optionally scroll to it
    const selectIndex = React.useCallback(
        (idx) => {
            setSelected(idx);
            if (scrollToSelected) scrollToIndex(idx);
        },
        [scrollToSelected, scrollToIndex]
    );

    // Sync with selectedIndex prop
    React.useEffect(() => {
        setSelected(selectedIndex);
    }, [selectedIndex]);

    // Scroll to selected item when selectedIndex changes or items load
    React.useEffect(() => {
        if (scrollToSelected && items.length > 0) {
            const timeout = setTimeout(() => scrollToIndex(selectedIndex), 100);

            return () => clearTimeout(timeout);
        }
    }, [selectedIndex, scrollToSelected, scrollToIndex, items.length]);

    // Arrow click - scroll by one item width, CSS snap handles alignment
    const scrollByOne = React.useCallback(
        (direction) => {
            const container = containerRef.current;
            const list = listRef.current;

            if (!container || !list) return;

            // Get item width from DOM
            const firstItem = list.querySelector('.CarouselButton');
            const scrollAmount = firstItem ? firstItem.offsetWidth + gap : container.clientWidth * 0.8;

            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: animateScroll ? 'smooth' : 'instant',
            });
        },
        [animateScroll, gap]
    );

    const scrollLeftFn = React.useCallback(() => scrollByOne('left'), [scrollByOne]);
    const scrollRightFn = React.useCallback(() => scrollByOne('right'), [scrollByOne]);

    const onClickItem = React.useCallback(
        (item, idx) => () => {
            selectIndex(idx);
            if (onSelectItem) onSelectItem(item, idx);
        },
        [onSelectItem, selectIndex]
    );

    // Compute CSS custom properties
    const cssVars = React.useMemo(() => {
        const vars = {
            '--carousel-gap': typeof gap === 'number' ? `${gap}px` : gap,
            '--carousel-padding': typeof padding === 'number' ? `${padding}px` : padding,
        };

        if (itemWidth) {
            vars['--carousel-item-width'] = typeof itemWidth === 'number' ? `${itemWidth}px` : itemWidth;
        } else if (itemsPerView) {
            // Use CSS calc for itemsPerView (padding is on outer wrapper, not in scroll area)
            const gapVal = typeof gap === 'number' ? `${gap}px` : gap;

            vars['--carousel-item-width'] = `calc((100% - ${gapVal} * ${itemsPerView - 1}) / ${itemsPerView})`;
        }

        return vars;
    }, [gap, padding, itemWidth, itemsPerView]);

    if (!items.length) return null;

    return (
        <div
            className={cx('Carousel', className, {
                'Carousel--dynamic-width': itemWidth || itemsPerView,
            })}
            id={id}
            style={{ ...cssVars, ...style }}
            {...props}
        >
            {canScrollLeft && (
                <button className="btn left" onClick={scrollLeftFn} aria-label={leftLbl}>
                    {BtnScrollLeft}
                </button>
            )}
            {canScrollRight && (
                <button className="btn right" onClick={scrollRightFn} aria-label={rightLbl}>
                    {BtnScrollRight}
                </button>
            )}
            <div className="Carousel__container" ref={containerRef}>
                <div
                    className={cx('Carousel__container__list', {
                        'Carousel__container__list--centered': centerCarousel,
                    })}
                    ref={listRef}
                >
                    {items.map((item, i) => (
                        <div
                            className={cx('CarouselButton', {
                                selected: selected === i,
                            })}
                            data-index={i}
                            onClick={onClickItem(item, i)}
                            role="button"
                            tabIndex="0"
                            key={getKey(item, i)}
                        >
                            <Item
                                {...itemProps}
                                item={item}
                                index={i}
                                selected={selected === i}
                                visibleIndices={visibleIndices}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

Carousel.propTypes = {
    animateScroll: px.bool,
    BtnScrollLeft: px.node,
    BtnScrollRight: px.node,
    className: px.string,
    gap: px.oneOfType([px.number, px.string]),
    id: px.string,
    Item: px.elementType,
    itemProps: px.objectOf(px.any),
    items: px.arrayOf(px.any),
    itemsPerView: px.number,
    itemWidth: px.oneOfType([px.number, px.string]),
    keyBy: px.oneOfType([px.func, px.string]),
    onScroll: px.func,
    onSelectItem: px.func,
    padding: px.oneOfType([px.number, px.string]),
    scrollToSelected: px.bool,
    selectedIndex: px.number,
    style: px.object,
};
